import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "../modules/auth/application/useAuth";
import { TherapistAPI } from "../modules/therapist/infrastructure/api/therapist.api";

import { Therapist } from "../modules/therapist/domain/Therapist";
import { TherapistContext } from "./TherapistContext";
import { useLanguageSync } from "./hooks/useLanguageSync";
import i18n from "../i18n";
import { setPatientTerm } from "../shared/i18n/patientTerm";

// eslint-disable-next-line react-refresh/only-export-components
export const useTherapist = () => useContext(TherapistContext);

export const TherapistProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const [therapist, setTherapistState] = useState<Therapist | null>(null);
  const [loadingTherapist, setLoadingTherapist] = useState(true);
  const fetchVersionRef = useRef(0);

  // Sync i18n language whenever the therapist changes — decoupled from the fetch
  useLanguageSync(therapist?.language ?? null);

  // Apply the therapist's preferred label for patients ("patient"/"client"/
  // "consultee") and re-render translated components with it.
  useEffect(() => {
    setPatientTerm(therapist?.patient_term ?? null);
    i18n.changeLanguage(i18n.language);
  }, [therapist?.patient_term]);

  // Exposed setTherapist invalidates any in-flight auto-fetch so it cannot overwrite.
  const setTherapist = useCallback((t: Therapist | null) => {
    fetchVersionRef.current++;
    setTherapistState(t);
    setLoadingTherapist(false);
  }, []);

  const refreshTherapist = useCallback(async () => {
    try {
      const response = await TherapistAPI.getMe();
      setTherapistState(response.data);
    } catch (e) {
      console.error("Error refreshing therapist", e);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setTherapistState(null);
      setLoadingTherapist(false);
      return;
    }

    const version = ++fetchVersionRef.current;

    async function fetchTherapist() {
      try {
        const tokenResult = await user!.getIdTokenResult();
        if (tokenResult.claims["role"] === "patient") {
          if (version === fetchVersionRef.current) {
            setTherapistState(null);
            setLoadingTherapist(false);
          }
          return;
        }
        const response = await TherapistAPI.getMe();
        if (version !== fetchVersionRef.current) return;
        setTherapistState(response.data);
      } catch (e) {
        console.error("Error loading therapist", e);
        if (version !== fetchVersionRef.current) return;
        setTherapistState(null);
      } finally {
        if (version === fetchVersionRef.current) {
          setLoadingTherapist(false);
        }
      }
    }

    fetchTherapist();
  }, [user]);

  return (
    <TherapistContext.Provider value={{ therapist, loadingTherapist, setTherapist, refreshTherapist }}>
      {children}
    </TherapistContext.Provider>
  );
};
